import "./Clients.scss";
import {ExternalLink, Globe, X} from "lucide-react";
import { useState } from "react";
import { VideoCard } from "../OurVideo/OurVideo";
import Card from "../../../Components/CardGallery/CardGallery";
import {useTranslation} from "react-i18next";
import {WorkItem} from "../../../types";

// ─── Types ───────────────────────────────────────────────

interface GalleryItem {
    immagini: string[];
    cliente: string;
    data: string;
}

type Client = {
    id: number
    image: string;
    name: string;
    description: string;
    color: string;
    is_highlighted: boolean;
    websiteUrl: string | null;
};

// ─── Componente icona fallback ──────────────────────────

function ClientIconFallback({ color }: { color: string }) {
    return (
        <div
            className="clients-card__icon-fallback"
            style={{ backgroundColor: `${color}22`, borderColor: color }}
        >
            <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke={color}
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
            </svg>
        </div>
    );
}

// ─── Componente card ─────────────────────────────────────

function ClientCard({
                        client,
                        onOpen,
                        workItems,
                        galleryData
                    }: {
    client: Client;
    onOpen: (client: Client) => void;
    workItems: WorkItem[]
    galleryData: GalleryItem[]
}) {

    const videos = workItems.filter(
        (item) => item.server.toLowerCase() === client.name.toLowerCase()
    );

    const graphic = galleryData.filter(
        (item) => item.cliente.toLowerCase() === client.name.toLowerCase()
    );


    const works = client.websiteUrl ? videos.length + graphic.length + 1 : videos.length + graphic.length
    const { t } = useTranslation();


    return (
        <article
            className="clients-card"
            onClick={() => onOpen(client)}
            style={{ "--server-color": client.color } as React.CSSProperties}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && onOpen(client)}
        >
            <div className="clients-card_container_info">
                <div className="clients-card__image">
                    {client.image ? (
                        <img src={client.image} alt={client.name} loading="lazy" />
                    ) : (
                        <ClientIconFallback color={client.color} />
                    )}
                </div>

                <div className="clients-card__content">
                    <h3 style={{ color: client.color }}>
                        {client.name}
                        {client.websiteUrl && (
                            <div className="clients-card__url"><Globe/></div>
                        )}
                    </h3>
                    <p>{client.description}</p> {/*Da fare traduzione backend*/}
                </div>
            </div>

            <div className="clients-card__footer">
                <span className="clients-card__number">
                  {works} {works === 1 ? t("works.clients.work") : t("works.clients.works")}
                </span>
                {client.is_highlighted && (
                    <span className="clients-card__highlighted">
                      {t("works.clients.featured")}
                    </span>
                )}
            </div>
        </article>
    );
}


function ClientModal({
                         client,
                         onClose,
                         workItems,
                         galleryData
                     }: {
    client: Client | null;
    onClose: () => void;
    workItems: WorkItem[];
    galleryData: GalleryItem[]
}) {
    const { t } = useTranslation();
    if (!client) return null;

    const videos = workItems.filter(
        (item) => item.server.toLowerCase() === client.name.toLowerCase()
    );

    const graphic = galleryData.filter(
        (item) => item.cliente.toLowerCase() === client.name.toLowerCase()
    );

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="clients-modal__overlay" onClick={handleBackdropClick}>
            <div className="clients-modal">
                <button
                    className="clients-modal__close"
                    onClick={onClose}
                    aria-label="Chiudi"
                >
                    <X size={24} />
                </button>

                <div className="clients-modal__body">
                    <div className="clients-modal__info">
                        <div className="clients-modal__image">
                            {client.image ? (
                                <img src={client.image} alt={client.name} />
                            ) : (
                                <div
                                    className="clients-modal__image-placeholder"
                                    style={{ backgroundColor: `${client.color}22` }}
                                >
                  <span style={{ color: client.color }}>
                    {client.name[0]}
                  </span>
                                </div>
                            )}
                        </div>
                        <div className="clients-modal__name">
                            <h2
                                style={{
                                    color: client.color,
                                    textShadow: `0 0 20px ${client.color}40`,
                                }}
                            >
                                {client.name}
                                {client.websiteUrl && (
                                    <a
                                        href={client.websiteUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="clients-modal__button"
                                        style={{
                                            backgroundColor: `${client.color}22`,
                                            borderColor: client.color,
                                            color: client.color,
                                        }}
                                    >
                                        <ExternalLink size={18} />
                                        Visita il sito
                                    </a>
                                )}
                            </h2>
                            <p className="clients-modal__description">
                                {client.description}
                            </p>
                        </div>
                    </div>

                    {videos.length > 0 && (
                        <div className="clients-modal__works">
                            <h4>{t("works.clients.videos")}</h4>
                            <ul className="clients-modal__video-list">
                                {videos.map((video, index) => (
                                    <VideoCard
                                        key={index}
                                        item={video}
                                        isVisible={true}
                                        onClick={() => {}}
                                        onMouseEnter={() => {}}
                                        onMouseLeave={() => {}}
                                    />
                                ))}
                            </ul>
                        </div>
                    )}
                    {graphic.length > 0 && (
                        <div className="clients-modal__works">
                            <h4>{t("works.clients.graphic")}</h4>
                            <ul className="clients-modal__video-list">
                                {graphic.map((item, index) => (
                                    <Card
                                        key={`${item.cliente}-${index}`}
                                        item={item}
                                    />
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Componente principale ──────────────────────────────

export default function OurClients({ workClients, workItems, galleryData }: {workClients: Client[], workItems: WorkItem[], galleryData: GalleryItem[] }) {
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const { t } = useTranslation();

    return (
        <section className="growth-clients">
            <div className="work-section__heading">

                <h2 className="work-section__title">{t("works.clients.title")}</h2>

            </div>
            <div className="growth-clients__container">
                <div className="clients-grid">
                    {workClients.map((client, index) => (
                        <ClientCard
                            key={`${client.name}-${index}`}
                            client={client}
                            onOpen={setSelectedClient}
                            workItems={workItems}
                            galleryData={galleryData}
                        />
                    ))}
                </div>
            </div>

            <ClientModal
                client={selectedClient}
                onClose={() => setSelectedClient(null)}
                workItems={workItems}
                galleryData={galleryData}
            />
        </section>
    );
}